import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getContact, updateContact, deleteContact, createInteraction, getTeamMembers, getMe } from '../utils/api';
import ContactFormModal from '../components/ContactFormModal';
import InteractionFormModal from '../components/InteractionFormModal';

const CATEGORY_LABELS = {
  lp_cogp: 'LP / Co-GP',
  lender: 'Lender',
  broker: 'Broker',
  partner_service: 'Partner / Service Provider',
  seller_operator: 'Seller / Operator',
};

function formatDateFull(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString([], {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function formatDateShort(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString([], {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [meData, setMeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showLogInteraction, setShowLogInteraction] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadContact = useCallback(async () => {
    try {
      const data = await getContact(id);
      setContact(data);
    } catch (err) {
      console.error('Load contact error:', err);
      if (err.status === 404) navigate('/contacts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadContact();
    getTeamMembers().then(res => setTeamMembers(res.data || res)).catch(console.error);
    getMe().then(setMeData).catch(console.error);
  }, [loadContact]);

  const handleUpdate = async (data) => {
    await updateContact(id, data);
    setShowEdit(false);
    loadContact();
  };

  const handleDelete = async () => {
    if (!window.confirm('Deactivate this contact? This can be reversed by an admin.')) return;
    setDeleting(true);
    try {
      await deleteContact(id);
      navigate('/contacts');
    } catch (err) {
      alert(err.message);
      setDeleting(false);
    }
  };

  const handleLogInteraction = async (data) => {
    await createInteraction(id, data);
    setShowLogInteraction(false);
    loadContact();
  };

  if (loading) return <div className="loading">Loading contact...</div>;
  if (!contact) return <div className="loading">Contact not found</div>;

  return (
    <div>
      <Link to="/contacts" className="back-link">← Back to Contacts</Link>

      {/* Contact header */}
      <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
          <div className={`contact-avatar ${contact.category}`} style={{ width: 52, height: 52, fontSize: '1.1rem' }}>
            {contact.first_name[0]}{contact.last_name[0]}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ fontFamily: 'var(--font-body)', fontSize: '1.4rem', fontWeight: 600, marginBottom: '2px' }}>
              {contact.first_name} {contact.last_name}
            </h1>
            {contact.title && <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>{contact.title}</div>}
            {contact.organization && (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', fontWeight: 500 }}>
                {contact.organization}
              </div>
            )}
            <div style={{ marginTop: 'var(--space-sm)' }}>
              <span className={`category-badge ${contact.category}`}>{CATEGORY_LABELS[contact.category]}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexShrink: 0 }}>
            <button className="btn btn-primary" onClick={() => setShowLogInteraction(true)}>
              Log Interaction
            </button>
            {/* Regular users only see Edit if they own this contact */}
            {meData && (meData.permissions?.can_edit_all ||
              contact.relationship_owner_id === meData.id ||
              contact.secondary_owners?.some(o => o.id === meData.id)) && (
              <button className="btn" onClick={() => setShowEdit(true)}>Edit</button>
            )}
          </div>
        </div>

        {/* Contact details grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--space-md)',
          marginTop: 'var(--space-lg)',
          paddingTop: 'var(--space-lg)',
          borderTop: '1px solid var(--color-border-light)',
        }}>
          <DetailField label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : null} />
          <DetailField label="Phone" value={contact.phone} href={contact.phone ? `tel:${contact.phone}` : null} />
          <DetailField label="Secondary Phone" value={contact.secondary_phone} />
          <DetailField label="Relationship Owner" value={contact.owner_name} />
          <DetailField label="Created" value={formatDateShort(contact.created_at)} />
          {(contact.city || contact.state) && (
            <DetailField
              label="Location"
              value={[contact.city, contact.state].filter(Boolean).join(', ')}
            />
          )}
        </div>

        {contact.notes && (
          <div style={{ marginTop: 'var(--space-lg)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--color-border-light)' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-xs)' }}>
              Notes
            </div>
            <div style={{ fontSize: '0.9rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{contact.notes}</div>
          </div>
        )}

        {contact.tags && contact.tags.length > 0 && (
          <div style={{ marginTop: 'var(--space-md)', display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
            {contact.tags.map((tag, i) => (
              <span key={i} style={{
                padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem',
                background: 'var(--color-bg)', color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)',
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Secondary owners */}
      {contact.secondary_owners && contact.secondary_owners.length > 0 && (
        <div style={{ marginTop: 'var(--space-md)' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 4 }}>
            Also Involved
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {contact.secondary_owners.map(o => (
              <span key={o.id} className="badge badge-neutral">{o.display_name}</span>
            ))}
          </div>
        </div>
      )}

      {/* Next follow-up */}
      {contact.next_follow_up && (() => {
        const d = new Date(contact.next_follow_up + 'T00:00:00');
        const today = new Date(); today.setHours(0,0,0,0);
        const daysUntil = Math.round((d - today) / 86400000);
        const overdue = daysUntil < 0;
        return (
          <div style={{ marginTop: 'var(--space-md)', padding: '10px 14px', borderRadius: 6,
            background: overdue ? 'rgba(196,92,92,0.08)' : 'rgba(196,160,78,0.06)',
            border: `1px solid ${overdue ? 'rgba(196,92,92,0.3)' : 'rgba(196,160,78,0.25)'}` }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
              color: overdue ? 'var(--color-danger)' : 'var(--color-warning)', marginBottom: 2 }}>
              {overdue ? '⚠️ Overdue Follow-up' : '📅 Next Follow-up'}
            </div>
            <div style={{ fontSize: '0.9rem', color: overdue ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
              {d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              <span style={{ marginLeft: 8, fontSize: '0.78rem', color: overdue ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>
                {overdue ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? 'Today' : `in ${daysUntil}d`}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Linked Events */}
      {contact.events && contact.events.length > 0 && (
        <div style={{ marginTop: 'var(--space-lg)' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
            Upcoming Events ({contact.events.length})
          </div>
          <div className="card" style={{ padding: 0 }}>
            {contact.events.map((ev, i) => {
              const hasMeeting = ev.meeting_date && ev.meeting_time;
              const today = new Date(); today.setHours(0,0,0,0);
              const daysAway = Math.round((new Date(ev.start_date + 'T00:00:00') - today) / 86400000);
              return (
                <div key={ev.id}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px' }}>
                    <div style={{ flex: 1 }}>
                      <Link to={`/events/${ev.id}`} style={{ fontWeight: 500, fontSize: '0.875rem', color: 'var(--color-text-primary)', textDecoration: 'none' }}>
                        {ev.name}
                      </Link>
                      <div style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        {new Date(ev.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {ev.city && ` · ${ev.city}${ev.state ? `, ${ev.state}` : ''}`}
                        {daysAway >= 0 && <span style={{ marginLeft: 8, color: 'var(--color-link)', fontSize: '0.72rem' }}>{daysAway === 0 ? 'Today' : `${daysAway}d away`}</span>}
                      </div>
                    </div>
                    {hasMeeting ? (
                      <span style={{ fontSize: '0.72rem', background: 'rgba(106,173,123,0.15)', color: 'var(--color-success)', padding: '2px 8px', borderRadius: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        Meeting confirmed
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>No meeting</span>
                    )}
                  </div>
                  {i < contact.events.length - 1 && <div style={{ height: 1, background: 'var(--color-border-faint)', margin: '0 16px' }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Linked Deals */}
      {contact.deals && contact.deals.length > 0 && (
        <div style={{ marginTop: 'var(--space-lg)' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
            Deals ({contact.deals.length})
          </div>
          <div className="card" style={{ padding: 0 }}>
            {contact.deals.map((deal, i) => {
              const statusColors = { active: 'badge-green', under_contract: 'badge-amber', closed: 'badge-neutral', dead: 'badge-red' };
              const statusLabels = { active: 'Active', under_contract: 'Under Contract', closed: 'Closed', dead: 'Dead' };
              const roleLabels = { equity: 'Equity', debt: 'Debt', broker: 'Broker', service: 'Service' };
              const displayValue = deal.purchase_price || deal.underwriting_price;
              return (
                <div key={deal.id}>
                  <Link to={`/deals/${deal.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', textDecoration: 'none', color: 'inherit' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem', color: 'var(--color-text-primary)' }}>{deal.name}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>{deal.address}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span className={`badge ${statusColors[deal.status] || 'badge-neutral'}`}>{statusLabels[deal.status] || deal.status}</span>
                      <span className="badge badge-neutral">{roleLabels[deal.role] || deal.role}</span>
                      {displayValue && <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>${parseFloat(displayValue).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>}
                    </div>
                  </Link>
                  {i < contact.deals.length - 1 && <div style={{ height: 1, background: 'var(--color-border-faint)', margin: '0 16px' }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Interaction History header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 'var(--space-lg) 0 var(--space-md)' }}>
        <h2 style={{ fontFamily: 'var(--font-body)', fontSize: '1.1rem' }}>
          Interaction History ({contact.interactions?.length || 0})
        </h2>
      </div>

      {!contact.interactions || contact.interactions.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h3>No interactions logged</h3>
            <p>Use the "Log Interaction" button to record calls, emails, and meetings.</p>
          </div>
        </div>
      ) : (
        contact.interactions.map(i => (
          <div key={i.id} className={`interaction-item ${i.interaction_type}`}>
            <div className="interaction-header">
              <span className="interaction-type">{i.interaction_type}</span>
              <span className="interaction-date">{formatDateFull(i.interaction_date)}</span>
            </div>
            <div className="interaction-summary">{i.summary}</div>
            <div className="interaction-meta">
              Logged by {i.logged_by_name}
              {i.deal_reference && <> · Deal: <strong>{i.deal_reference}</strong></>}
              {i.source !== 'manual' && <> · Source: {i.source}</>}
            </div>
          </div>
        ))
      )}

      {/* Danger zone — admin only */}
      <div style={{ marginTop: 'var(--space-2xl)', paddingTop: 'var(--space-lg)', borderTop: '1px solid var(--color-border-light)' }}>
        {meData?.role === 'admin' && (
        <><button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
          {deleting ? 'Deactivating...' : 'Deactivate Contact'}
        </button>
        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 'var(--space-sm)' }}>
          Admin only — soft-deletes the contact record
        </span></>)}
      </div>

      {/* Modals */}
      {showEdit && (
        <ContactFormModal
          contact={contact}
          teamMembers={teamMembers}
          onSave={handleUpdate}
          onClose={() => setShowEdit(false)}
        />
      )}
      {showLogInteraction && (
        <InteractionFormModal
          onSave={handleLogInteraction}
          onClose={() => setShowLogInteraction(false)}
          contactEvents={contact.events || []}
        />
      )}
    </div>
  );
}

function DetailField({ label, value, href }) {
  if (!value) return null;
  return (
    <div>
      <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: '2px' }}>
        {label}
      </div>
      {href ? (
        <a href={href} style={{ fontSize: '0.9rem', color: 'var(--color-accent)', textDecoration: 'none' }}>{value}</a>
      ) : (
        <div style={{ fontSize: '0.9rem' }}>{value}</div>
      )}
    </div>
  );
}
